import {
  Music,
  Theater,
  Mic2,
  Dumbbell,
  MapPin,
  Tag,
  Presentation,
  Baby,
  BookOpen, Users, GraduationCap, Building2, Handshake,
  ShoppingBag,
  Sparkles,
  type LucideIcon,
} from "lucide-react";

export interface Category {
  value: string;
  label: string;
  icon: LucideIcon;
  description?: string;
  labelEn?: string;
}

/**
 * Central source of truth for event categories.
 * Used across landing page, producer form, and event filters.
 */
export const LEGACY_EVENT_CATEGORIES: Category[] = [
  { 
    value: "shows", 
    label: "Festas e Shows", 
    icon: Music,
    description: "Shows, festas, baladas e eventos musicais"
  },
  { 
    value: "theater", 
    label: "Teatros e Espetáculos", 
    icon: Theater,
    description: "Peças teatrais, musicais e espetáculos"
  },
  { 
    value: "standup", 
    label: "Stand Up Comedy", 
    icon: Mic2,
    description: "Shows de comédia e humor"
  },
  { 
    value: "sports", 
    label: "Esportes", 
    icon: Dumbbell,
    description: "Jogos, competições e eventos esportivos"
  },
  { 
    value: "tours", 
    label: "Passeios e Tours", 
    icon: MapPin,
    description: "Passeios turísticos e experiências"
  },
  { 
    value: "deals", 
    label: "Descontos Exclusivos", 
    icon: Tag,
    description: "Eventos com ofertas e promoções especiais"
  },
  { 
    value: "corporate", 
    label: "Congressos e Palestras", 
    icon: Presentation,
    description: "Eventos corporativos, palestras e workshops"
  },
  { 
    value: "kids", 
    label: "Infantil", 
    icon: Baby,
    description: "Eventos para crianças e famílias"
  },
  { 
    value: "shopping", 
    label: "Eventos com Loja", 
    icon: ShoppingBag,
    description: "Feiras, bazares e eventos com vendas"
  },
  { 
    value: "other", 
    label: "Outros", 
    icon: Sparkles,
    description: "Outros tipos de eventos"
  },
];

/** New persisted values. The backend capability must be checked before offering writes. */
export const EVENT_CATEGORIES: Category[] = [
  { value: "workshops", label: "Workshops", labelEn: "Workshops", icon: BookOpen },
  { value: "palestras", label: "Palestras", labelEn: "Talks", icon: Mic2 },
  { value: "summits", label: "Summits", labelEn: "Summits", icon: Users },
  { value: "congressos", label: "Congressos", labelEn: "Conferences", icon: Presentation },
  { value: "treinamentos", label: "Treinamentos", labelEn: "Training", icon: GraduationCap },
  { value: "convencoes", label: "Convenções", labelEn: "Conventions", icon: Building2 },
  { value: "networking", label: "Networking", labelEn: "Networking", icon: Handshake },
  { value: "other", label: "Outros eventos", labelEn: "Other events", icon: Sparkles },
];

/** Keep historical values readable and filterable until all clients/events migrate. */
export const ALL_EVENT_CATEGORIES: Category[] = [
  ...EVENT_CATEGORIES,
  ...LEGACY_EVENT_CATEGORIES.filter(c => !EVENT_CATEGORIES.some(n => n.value === c.value)),
];

export function getEventCategoryChoices(corporateEnabled: boolean, currentValue = ""): Category[] {
  const choices = corporateEnabled ? EVENT_CATEGORIES : LEGACY_EVENT_CATEGORIES;
  if (!currentValue || choices.some(c => c.value === currentValue)) return choices;
  // Editing never silently changes an existing event's classification.
  const current = getCategoryByValue(currentValue) ?? { value: currentValue, label: currentValue, icon: Sparkles };
  return [current, ...choices];
}

/**
 * Get category by value
 */
export function getCategoryByValue(value: string): Category | undefined {
  return ALL_EVENT_CATEGORIES.find((c) => c.value === value);
}

/**
 * Get category label by value
 */
export function getCategoryLabel(value: string, language = "pt"): string {
  const category = getCategoryByValue(value);
  return (language.startsWith("en") ? category?.labelEn : category?.label) || category?.label || value;
}

/**
 * Categories for select inputs (includes "All" option)
 */
export const CATEGORY_OPTIONS = [
  { value: "", label: "Todas as categorias" },
  ...ALL_EVENT_CATEGORIES.map((c) => ({ value: c.value, label: c.label })),
];
